import React, { useMemo, useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow, Typography, TextField, Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useGetEnvListQuery, useSetEnvVarMutation } from "../../api/env";
import Preloader from "../Common/Preloader";

interface EnvVar {
    key: string;
    value: string;
}

const domainGroups: Record<string, string[]> = {
    "Системные домены": ["API_HOST", "HTML5_HOST", "ENDORPHINA_HOST", "GAME_HOST", "INBET_VEGAS_HOST", "INBET_BET_HOST", "INBET_KENO_SOURCE_URL"],
    "Домены лобби": ["DEFAULT_HOST", "GOLD_HOST", "RED_HOST", "BLUE_HOST", "LOTO_HOST", "ARENA_HOST", "CES_HOST", "TADJ_HOST", "KENO_HOST", "ONLINE_HOST", "YEP_HOST"],
    "Опросы": ["POLL_HOST", "POLL_PROJECT_ID"],
    "Пушер": ["PUSHER_KEY", "PUSHER_HTTP_HOST", "PUSHER_SOCKET_HOST", "PUSHER_SECRET", "PUSHER_APP_ID", "PUSHER_CLUSTER"],
    "SMS": ["SMS_INTERVAL", "SMS_DAY_LIMIT", "CONFIRMATION_OPERATOR"],
    "Main SMS": ["MAIN_SMS_HOST", "MAIN_SMS_API_KEY", "MAIN_SMS_PROJECT"],
    "Green SMS": ["GREEN_SMS_HOST", "GREEN_SMS_API_KEY"],
    "SMSC": ["SMSC_HOST", "SMSC_API_KEY", "SMSC_SENDER"],
    "Email": ["EMAIL_INTERVAL", "EMAIL_DAY_LIMIT", "EMAIL_FROM", "EMAIL_REPLY_TO", "EMAIL_HOST", "EMAIL_USERNAME", "EMAIL_PASSWORD"],
    "Чемпион": ["CHCBLACK_WALLET", "CHCBLACK_URL"],
    "c2 (GMNGGATE)": ["GMNGGATESERVICE_URL", "GMNGGATESERVICE_CERT", "GMNGGATESERVICE_KEY", "GMNGGATESERVICE_PASSPHRASE", "GMNGGATESERVICE_REPLACE_FROM", "GMNGGATESERVICE_REPLACE_TO", "GMNGGATESERVICE_LOG_ENABLED"],
    "Платёжка btqfi.com": ["BTQFI_HOST", "BTQFI_API_KEY"],
    "Платёжка aeterna.biz": ["AETERNA_HOST", "AETERNA_PUBLIC_TOKEN", "AETERNA_SECRET_TOKEN", "AETERNA_CALLBACK_TOKEN", "AETERNA_EMAIL"],
    "Платёжка skyphoenix.net": ["SKYPHOENIX_HOST", "SKYPHOENIX_PUBLIC_TOKEN", "SKYPHOENIX_SECRET_TOKEN", "SKYPHOENIX_CALLBACK_TOKEN", "SKYPHOENIX_EMAIL"],
    "lvslot": ["LVSLOT_LOG_ENABLED", "LVSLOT_URL", "LVSLOT_AGENT_ID", "LVSLOT_API_KEY", "LVSLOT_HALLS"],
};
const OTHER_GROUP = "Прочее";

const Vars: React.FC = () => {
    const [t] = useTranslation();
    const { data: envVars, isLoading, isSuccess } = useGetEnvListQuery();
    const [setEnvVar, { isLoading: isSetting }] = useSetEnvVarMutation();
    const [values, setValues] = useState<Record<string, string>>({});

    useEffect(() => {
        if (isSuccess && envVars) {
            setValues(envVars.reduce((acc, v) => ({ ...acc, [v.key]: v.value }), {}));
        }
    }, [isSuccess, envVars]);

    const handleValueChange = (key: string, value: string) => {
        setValues(prev => ({ ...prev, [key]: value }));
    };

    const groupedVars = useMemo(() => {
        if (!envVars) {
            return {};
        }

        const grouped: Record<string, EnvVar[]> = {};
        Object.keys(domainGroups).forEach(group => grouped[group] = []);
        grouped[OTHER_GROUP] = [];

        envVars.forEach(envVar => {
            let found = false;
            for (const group in domainGroups) {
                if (domainGroups[group].includes(envVar.key)) {
                    grouped[group].push(envVar);
                    found = true;
                    break;
                }
            }
            if (!found) {
                grouped[OTHER_GROUP].push(envVar);
            }
        });

        return grouped;
    }, [envVars]);

    if (isLoading) {
        return <Preloader />;
    }

    const renderTable = (title: string, vars: EnvVar[]) => (
        <div style={{ marginBottom: "2rem" }}>
            <Typography variant="h5" gutterBottom>{title}</Typography>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{t("ru.vars.key")}</TableCell>
                        <TableCell colSpan={2}>{t("ru.vars.value")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {vars.map((envVar) => <TableRow key={envVar.key}>
                        <TableCell>{envVar.key}</TableCell>
                        <TableCell>
                            <TextField
                                fullWidth
                                value={values[envVar.key] || ""}
                                onChange={(e) => handleValueChange(envVar.key, e.target.value)}
                            />
                        </TableCell>
                        <TableCell>
                            <Button disabled={isSetting} variant="contained" color="primary" onClick={() => setEnvVar({ key: envVar.key, value: values[envVar.key] })}>Сохранить</Button>
                        </TableCell>
                    </TableRow>)}
                </TableBody>
            </Table>
        </div>
    );

    return <>
        <h2>{t("ru.admin_menu.vars")}</h2>
        {Object.entries(groupedVars).map(([groupName, vars]) =>
            vars.length > 0 && renderTable(groupName, vars)
        )}
    </>;
};

export default Vars;
